(() => {
  const MOBILE_TRIGGER_SELECTOR = '.header-icon--hambuger .js-popup-toggle[aria-controls="mobileMenu"]';
  const MOBILE_SHELL_SELECTOR = '#mobileMenu';
  const MOBILE_PANEL_SELECTOR = '#mobileMenuPanel';
  const DESKTOP_ITEM_SELECTOR = '.main-navigation > .list-item';
  const DESKTOP_FLYOUT_SELECTOR = '.mega__section, .dropdown';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.closest('[hidden], [aria-hidden="true"]')) return false;

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const getFocusable = (container) => {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
  };

  const initMobileMenu = () => {
    const trigger = document.querySelector(MOBILE_TRIGGER_SELECTOR);
    const shell = document.querySelector(MOBILE_SHELL_SELECTOR);
    if (!(trigger instanceof HTMLButtonElement) || !(shell instanceof HTMLElement)) return;

    const desktopMedia = window.matchMedia('(min-width: 992px)');
    let panel = null;
    let returnTarget = null;
    let wasOpen = false;
    let focusDesktopAfterClose = false;
    let lastFocusWasInPanel = false;
    let focusSettleTimer = null;
    let focusFinalTimer = null;
    let focusTransitionElement = null;
    let focusTransitionHandler = null;

    const clearFocusWork = () => {
      if (focusSettleTimer !== null) window.clearTimeout(focusSettleTimer);
      if (focusFinalTimer !== null) window.clearTimeout(focusFinalTimer);
      if (focusTransitionElement && focusTransitionHandler) {
        focusTransitionElement.removeEventListener('transitionend', focusTransitionHandler);
      }

      focusSettleTimer = null;
      focusFinalTimer = null;
      focusTransitionElement = null;
      focusTransitionHandler = null;
    };

    const getDesktopReturnTarget = () => {
      const navigationLinks = document.querySelectorAll(
        '.section-navigation .main-navigation > .list-item > a[href]'
      );
      const navigationTarget = Array.from(navigationLinks).find(isVisible);
      if (navigationTarget) return navigationTarget;

      const logoLinks = document.querySelectorAll(
        '[data-section-type="header-section"] .logo-container > a[href]'
      );
      return Array.from(logoLinks).find(isVisible) || null;
    };

    const isOpen = () => {
      return Boolean(
        panel &&
        panel.classList.contains('active') &&
        panel.getAttribute('aria-hidden') === 'false'
      );
    };

    const closePanel = () => {
      if (!panel) return;
      clearFocusWork();

      if (typeof panel.close === 'function') {
        panel.close();
      } else {
        panel.classList.remove('active', 'mobile-menu');
        panel.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('show-popup');
      }
    };

    const handlePanelKeydown = (event) => {
      if (!isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closePanel();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable(panel);
      if (!focusable.length) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };

    const bindPanel = () => {
      const nextPanel = shell.querySelector(MOBILE_PANEL_SELECTOR);
      if (!(nextPanel instanceof HTMLElement) || nextPanel === panel) return;

      panel = nextPanel;
      panel.addEventListener('keydown', handlePanelKeydown);
    };

    const focusOpenPanel = () => {
      bindPanel();
      if (!isOpen() || panel.contains(document.activeElement)) return true;

      const initialFocus = panel.querySelector('.jsClose') || getFocusable(panel)[0] || panel;
      if (initialFocus === panel && !panel.hasAttribute('tabindex')) {
        panel.setAttribute('tabindex', '-1');
      }
      initialFocus.focus({ preventScroll: true });
      return panel.contains(document.activeElement);
    };

    const scheduleSettledFocus = () => {
      clearFocusWork();
      bindPanel();
      if (!isOpen() || panel.contains(document.activeElement)) return;

      const transitionElement = panel.querySelector('.popup__content') || panel;
      const finish = (event) => {
        if (event && event.target !== transitionElement) return;
        clearFocusWork();
        if (focusOpenPanel()) return;

        focusFinalTimer = window.setTimeout(() => {
          focusFinalTimer = null;
          focusOpenPanel();
        }, 100);
      };

      focusTransitionElement = transitionElement;
      focusTransitionHandler = finish;
      transitionElement.addEventListener('transitionend', finish);
      focusSettleTimer = window.setTimeout(finish, 450);
    };

    const syncState = () => {
      bindPanel();
      const open = isOpen();
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      const shellHidden = open ? 'false' : 'true';
      if (shell.getAttribute('aria-hidden') !== shellHidden) {
        shell.setAttribute('aria-hidden', shellHidden);
      }

      if (open && (!wasOpen || !panel.contains(document.activeElement))) {
        if (!focusOpenPanel()) scheduleSettledFocus();
      } else if (!open || panel.contains(document.activeElement)) {
        clearFocusWork();
      }

      if (!open && (wasOpen || focusDesktopAfterClose)) {
        const shouldFocusDesktop = focusDesktopAfterClose;
        focusDesktopAfterClose = false;
        const nextFocus = returnTarget;
        returnTarget = null;
        window.requestAnimationFrame(() => {
          if (shouldFocusDesktop) {
            const desktopTarget = getDesktopReturnTarget();
            if (desktopTarget instanceof HTMLElement && document.contains(desktopTarget)) {
              desktopTarget.focus({ preventScroll: true });
            }
            return;
          }

          if (nextFocus instanceof HTMLElement && document.contains(nextFocus)) {
            nextFocus.focus({ preventScroll: true });
          }
        });
      }

      wasOpen = open;
    };

    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('click', () => {
      clearFocusWork();
      focusDesktopAfterClose = false;
      lastFocusWasInPanel = false;
      returnTarget = trigger;
    });
    document.addEventListener('focusin', (event) => {
      bindPanel();
      if (event.target === document.body || event.target === document.documentElement) return;
      lastFocusWasInPanel = Boolean(panel && panel.contains(event.target));
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isOpen() || panel.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      closePanel();
    });

    const handleDesktopChange = () => {
      if (!desktopMedia.matches) return;
      bindPanel();
      if (!isOpen()) return;

      const focusWasInPanel = panel.contains(document.activeElement) || lastFocusWasInPanel;
      lastFocusWasInPanel = false;
      clearFocusWork();
      returnTarget = null;
      focusDesktopAfterClose = focusWasInPanel;
      closePanel();
    };

    if (typeof desktopMedia.addEventListener === 'function') {
      desktopMedia.addEventListener('change', handleDesktopChange);
    } else if (typeof desktopMedia.addListener === 'function') {
      desktopMedia.addListener(handleDesktopChange);
    }

    const observer = new MutationObserver(syncState);
    observer.observe(shell, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class'],
      childList: true,
      subtree: true
    });

    syncState();
  };

  const getDirectFlyout = (item) => {
    return Array.from(item.children).find((child) => child.matches(DESKTOP_FLYOUT_SELECTOR)) || null;
  };

  const initDesktopMenuItem = (item, index) => {
    const trigger = Array.from(item.children).find((child) => child.matches('a.list-item-link'));
    const hasFlyout = item.classList.contains('has-dropdown') || item.querySelector(':scope > .jsTemplate');
    if (!(trigger instanceof HTMLAnchorElement) || !hasFlyout) return;

    let dismissed = false;
    let lastPointerType = '';
    const isBreaker = trigger.getAttribute('href') === '/collections/circuit-breakers';
    let openTimer = null;
    let closeTimer = null;
    let hoverCloseTimer = null;
    const clearHoverCloseTimer = () => {
      if (hoverCloseTimer === null) return;
      clearTimeout(hoverCloseTimer);
      hoverCloseTimer = null;
    };
    const clearMenuTimers = () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      clearHoverCloseTimer();
      openTimer = closeTimer = null;
    };

    const ensureFlyout = () => {
      const flyout = getDirectFlyout(item);
      if (!(flyout instanceof HTMLElement)) return null;

      if (trigger.nextElementSibling !== flyout) {
        flyout.before(trigger);
      }

      if (!flyout.id) {
        flyout.id = `HeaderMenu-${index + 1}`;
      }

      trigger.setAttribute('aria-controls', flyout.id);
      return flyout;
    };

    const setOpen = (open) => {
      const flyout = ensureFlyout();
      const expanded = Boolean(open && flyout && !dismissed);
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      flyout?.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      if (isBreaker) {
        item.classList.toggle('breaker-menu-open', expanded);
        if (flyout) flyout.inert = !expanded;
      }
    };

    const syncState = () => {
      // A touch-open item is not driven by :hover / focus-within -- leave it
      // alone here so mouseleave/focusout on other items cannot close it.
      if (item.classList.contains('is-touch-open')) return;
      const focused = item.contains(document.activeElement);
      const hovered = item.matches(':hover');
      if (!isBreaker) { setOpen(hovered || focused); return; }
      if (dismissed) { clearMenuTimers(); setOpen(false); return; }
      if (focused) { clearMenuTimers(); setOpen(true); return; }
      if (hovered) {
        clearTimeout(closeTimer);
        closeTimer = null;
        if (trigger.getAttribute('aria-expanded') === 'true' || openTimer) return;
        openTimer = setTimeout(() => { openTimer = null; setOpen(item.matches(':hover')); }, HOVER_OPEN_DELAY);
      } else {
        clearTimeout(openTimer);
        openTimer = null;
        if (!closeTimer) closeTimer = setTimeout(() => { closeTimer = null; setOpen(false); }, 200);
      }
    };

    const reopen = () => {
      clearHoverCloseTimer();
      dismissed = false;
      item.classList.remove('header-menu-is-dismissed');
      window.requestAnimationFrame(syncState);
    };

    const openTouch = () => {
      reopen();
      item.classList.add('is-touch-open');
      setOpen(true);
      if (!getDirectFlyout(item)) {
        // The .jsTemplate placeholder has not been swapped for real flyout
        // markup yet -- theme.js onNavHover does that on mouseenter/focusin,
        // which a touch tap does not reliably fire. Trigger it ourselves,
        // then re-run setOpen (which re-runs ensureFlyout) once it lands.
        trigger.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
        window.requestAnimationFrame(() => setOpen(true));
      }
    };

    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('pointerdown', (event) => {
      lastPointerType = event.pointerType;
    });
    trigger.addEventListener('click', (event) => {
      // Consume the flag so it only ever describes the pointer that caused
      // THIS click. Left unconsumed, a stale 'touch' from an earlier tap
      // would make a later keyboard Enter (a synthetic click with no
      // pointerdown, pointerType '') look like a touch tap too, and open
      // the flyout instead of letting Enter navigate.
      const pointerType = lastPointerType;
      lastPointerType = '';
      // The label is a real link. Hover or ArrowDown opens its menu.
      if (isBreaker) return;
      const isTouchPointer = pointerType === 'touch' || pointerType === 'pen';
      if (!isTouchPointer || !hasFlyout || item.classList.contains('is-touch-open')) return;
      // First touch tap: open the flyout instead of following the link. A
      // second tap on the same trigger skips this (already touch-open) and
      // the link navigates normally.
      event.preventDefault();
      openTouch();
    });
    const dismissOutside = (event) => {
      if (isBreaker && !item.contains(event.target)) {
        clearMenuTimers();
        dismissed = true;
        item.classList.remove('breaker-menu-click-open');
        item.classList.add('header-menu-is-dismissed');
        setOpen(false);
        return;
      }
      if (!item.classList.contains('is-touch-open') || item.contains(event.target)) return;
      item.classList.remove('is-touch-open');
      setOpen(false);
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    document.addEventListener('click', dismissOutside, true);
    item.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!isBreaker || !link || link === trigger) return;
      clearMenuTimers();
      dismissed = true;
      item.classList.remove('breaker-menu-click-open');
      item.classList.add('header-menu-is-dismissed');
      setOpen(false);
    });
    trigger.addEventListener('mouseenter', reopen);
    item.addEventListener('mouseenter', reopen);
    // A pointer leaving the trigger toward the panel crosses a bridging gap
    // that has no element of its own, so containment checks against
    // event.target cannot see it. Track the pointer against a corridor
    // (trigger rect + panel rect + the rect bridging them) instead, and
    // debounce the close so crossing the gap at a normal speed keeps the
    // menu open. This is a hover-out close, never a dismissal: it must not
    // set `dismissed`, or the menu could not reopen on the next hover.
    const pointInRect = (x, y, rect) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
    const isInsideHoverCorridor = (x, y) => {
      const triggerRect = trigger.getBoundingClientRect();
      if (pointInRect(x, y, triggerRect)) return true;
      const panel = getDirectFlyout(item)?.querySelector('.section-container');
      if (!(panel instanceof HTMLElement)) return false;
      const panelRect = panel.getBoundingClientRect();
      if (pointInRect(x, y, panelRect)) return true;
      const bridgeTop = Math.min(triggerRect.bottom, panelRect.top);
      const bridgeBottom = Math.max(triggerRect.bottom, panelRect.top);
      return x >= panelRect.left && x <= panelRect.right && y >= bridgeTop && y <= bridgeBottom;
    };
    const HOVER_OPEN_DELAY = 120;
    const HOVER_CLOSE_DELAY = 150;
    const closeBreakerMenuOnHoverOut = () => {
      hoverCloseTimer = null;
      item.classList.remove('breaker-menu-click-open', 'is-touch-open');
      setOpen(false);
    };
    const scheduleHoverClose = () => {
      if (hoverCloseTimer !== null) return;
      hoverCloseTimer = setTimeout(closeBreakerMenuOnHoverOut, HOVER_CLOSE_DELAY);
    };
    item.addEventListener('mouseleave', () => {
      if (isBreaker) scheduleHoverClose();
      else window.requestAnimationFrame(syncState);
    });
    if (isBreaker) {
      document.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch' || trigger.getAttribute('aria-expanded') !== 'true') return;
        if (isInsideHoverCorridor(event.clientX, event.clientY)) {
          clearHoverCloseTimer();
          return;
        }
        scheduleHoverClose();
      }, { passive: true });
    }
    item.addEventListener('focusin', reopen);
    item.addEventListener('focusout', () => window.setTimeout(syncState, 0));
    item.addEventListener('keydown', (event) => {
      if (isBreaker && event.target === trigger && [' ', 'ArrowDown'].includes(event.key)) {
        event.preventDefault();
        clearMenuTimers();
        dismissed = false;
        item.classList.remove('header-menu-is-dismissed');
        if (!getDirectFlyout(item)) trigger.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
        setOpen(true);
        if (event.key === 'ArrowDown') getDirectFlyout(item)?.querySelector('a')?.focus();
        return;
      }
      if (event.key !== 'Escape' || trigger.getAttribute('aria-expanded') !== 'true') return;

      event.preventDefault();
      event.stopPropagation();
      trigger.focus({ preventScroll: true });
      dismissed = true;
      clearMenuTimers();
      item.classList.remove('breaker-menu-click-open');
      item.classList.remove('is-touch-open');
      item.classList.add('header-menu-is-dismissed');
      setOpen(false);
    });

    const observer = new MutationObserver(() => {
      ensureFlyout();
      syncState();
    });
    observer.observe(item, { childList: true });

    ensureFlyout();
  };

  const initDesktopMenus = () => {
    const items = document.querySelectorAll(DESKTOP_ITEM_SELECTOR);
    for (const [index, item] of items.entries()) {
      if (item instanceof HTMLElement) initDesktopMenuItem(item, index);
    }
  };

  initMobileMenu();
  initDesktopMenus();
})();
